import React, {
  createContext,
  useContext,
  useState,
  type FC,
  type PropsWithChildren,
} from 'react'
import {LANGS, type LangType} from 'src/lib/translates/types'

type MutateActionType = 'add' | 'update' | 'delete' | 'none'

export type ValuesType = LangType | null

export const isValuesType = (value: string | null): value is ValuesType => {
  if (value === LANGS.RU) return true
  if (value === LANGS.EN) return true
  if (value === LANGS.UK) return true
  if (value === LANGS.TR) return true
  return false
}

type AdminContextType = {
  lang: ValuesType
  riddle: string
  answer: string
  slug: string
  finished: boolean
  totalCount: number
  langForm: ValuesType
  riddleForm: string
  answerForm: string
  slugForm: string
  idRiddleForm: string
  mutateAction: MutateActionType
  setFinished: (value: boolean) => void
  setLang: (value: ValuesType) => void
  setRiddle: (value: string) => void
  setAnswer: (value: string) => void
  setSlug: (value: string) => void
  setTotalCount: (value: number) => void
  setLangForm: (val: ValuesType) => void
  setRiddleForm: (val: string) => void
  setAnswerForm: (val: string) => void
  setSlugForm: (val: string) => void
  setIdRiddleForm: (val: string) => void
  setMutateAction: (val: MutateActionType) => void
}

const AdminContext = createContext<AdminContextType | undefined>(undefined)

export const useAdminContext = () => {
  const context = useContext(AdminContext)
  if (!context) {
    throw new Error('useAdminContext must be used within a AdminProvider')
  }
  return context
};

export const AdminProvider: FC<PropsWithChildren> = ({ children }) => {
  const [lang, setLang] = useState<ValuesType>(null)
  const [riddle, setRiddle] = useState('')
  const [answer, setAnswer] = useState('')
  const [slug, setSlug] = useState('')
  const [finished, setFinished] = useState(false)
  const [totalCount, setTotalCount] = useState(0)

  const [langForm, setLangForm] = useState<ValuesType>(null)
  const [riddleForm, setRiddleForm] = useState<string>('')
  const [answerForm, setAnswerForm] = useState<string>('')
  const [slugForm, setSlugForm] = useState<string>('')
  const [idRiddleForm, setIdRiddleForm] = useState<string>('')
  const [mutateAction, setMutateAction] = useState<MutateActionType>('none')

  const value = {
    lang,
    riddle,
    answer,
    slug,
    finished,
    totalCount,
    langForm,
    riddleForm,
    answerForm,
    slugForm,
    idRiddleForm,
    mutateAction,
    setFinished: (val: boolean) => setFinished(val),
    setLang: (val: ValuesType) => setLang(val),
    setRiddle: (val: string) => setRiddle(val),
    setAnswer: (val: string) => setAnswer(val),
    setSlug: (val: string) => setSlug(val),
    setTotalCount: (val: number) => setTotalCount(val),
    setLangForm: (val: ValuesType) => setLangForm(val),
    setRiddleForm: (val: string) => setRiddleForm(val),
    setAnswerForm: (val: string) => setAnswerForm(val),
    setSlugForm: (val: string) => setSlugForm(val),
    setIdRiddleForm: (val: string) => setIdRiddleForm(val),
    setMutateAction: (val: MutateActionType) => setMutateAction(val),
  }

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  )
}
